import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaCreditCard, FaCheckCircle, FaUsers, FaCalendarAlt,
  FaSpinner, FaEuroSign, FaHistory, FaDownload
} from 'react-icons/fa';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';

// Fonction pour télécharger un fichier PDF avec authentification
const downloadPDF = async (endpoint, filename) => {
  try {
    const response = await api.get(endpoint, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Guide téléchargé !');
  } catch (error) {
    console.error('Erreur téléchargement:', error);
    toast.error('Erreur lors du téléchargement');
  }
};

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [calculating, setCalculating] = useState(false);
  const [pricingConfig, setPricingConfig] = useState(null); // Tarifs chargés depuis l'API
  
  const [formData, setFormData] = useState({
    region: 'france',
    studentCount: 10
  });
  const [pricing, setPricing] = useState(null);

  // Charger les tarifs depuis le backend (source unique de vérité)
  useEffect(() => {
    const fetchPricingConfig = async () => {
      try {
        const response = await api.get('/settings/pricing');
        setPricingConfig(response.data.regions);
      } catch (error) {
        console.error('Erreur chargement tarifs:', error);
        toast.error('Impossible de charger les tarifs');
      }
    };
    fetchPricingConfig();
    fetchSubscriptionData();
  }, []);

  useEffect(() => {
    if (formData.studentCount > 0 && pricingConfig) {
      calculatePricing();
    }
  }, [formData.region, formData.studentCount, pricingConfig]);

  const fetchSubscriptionData = async () => {
    try {
      const [subResponse, transResponse] = await Promise.all([
        api.get('/api/payments/subscription/status'),
        api.get('/api/payments/transactions')
      ]);
      
      setSubscription(subResponse.data.subscription);
      setTransactions(transResponse.data.transactions || []);
    } catch (error) {
      console.error('Erreur chargement abonnement:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePricing = async () => {
    if (formData.studentCount < 1) return;
    
    setCalculating(true);
    try {
      const response = await api.post('/api/settings/pricing/calculate', {
        region: formData.region,
        studentCount: parseInt(formData.studentCount)
      });
      setPricing(response.data);
    } catch (error) {
      console.error('Erreur calcul prix:', error);
    } finally {
      setCalculating(false);
    }
  };

  const handleStripeCheckout = async () => {
    try {
      const response = await api.post('/api/payments/stripe/create-session', {
        region: formData.region,
        studentCount: parseInt(formData.studentCount),
        originUrl: window.location.origin
      });
      
      if (response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      }
    } catch (error) {
      console.error('Erreur Stripe:', error);
      toast.error('Erreur lors de la création du paiement');
    }
  };

  const handlePayPalCheckout = async () => {
    try {
      setCalculating(true);
      const response = await api.post('/payments/paypal/create-order', {
        region: formData.region,
        studentCount: parseInt(formData.studentCount)
      });
      
      if (response.data.approvalUrl) {
        // Sauvegarder l'orderId pour la capture après retour
        localStorage.setItem('pending_paypal_order', response.data.orderId);
        // Rediriger vers PayPal
        window.location.href = response.data.approvalUrl;
      } else {
        toast.error('Erreur: URL PayPal non reçue');
      }
    } catch (error) {
      console.error('Erreur PayPal:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement PayPal');
    } finally {
      setCalculating(false);
    }
  };

  if (loading || !pricingConfig) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-[#1B4965]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1B4965] to-[#2D5A7B] text-white py-6 px-4">
        <div className="container mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <FaArrowLeft /> Retour
          </button>
          <h1 className="text-2xl md:text-3xl font-bold">Abonnement</h1>
          <p className="text-white/70 mt-1">Gérez votre abonnement DailyGlobe School</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Abonnement actuel */}
          <div className="lg:col-span-2 space-y-6">
            
            {subscription?.status === 'active' ? (
              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                      <FaCheckCircle className="text-white text-xl" />
                    </div>
                    <div>
                      <CardTitle className="text-green-800">Abonnement Actif</CardTitle>
                      <CardDescription className="text-green-600">
                        {subscription.studentCount} étudiants • {subscription.region === 'afrique' ? 'Tarif Afrique' : 'Tarif Europe'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-green-600">Dernier paiement :</span>
                      <p className="font-medium text-green-800">
                        {subscription.lastPayment ? new Date(subscription.lastPayment).toLocaleDateString('fr-FR') : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className="text-green-600">Prochaine facturation :</span>
                      <p className="font-medium text-green-800">
                        {subscription.nextBilling || 'Mensuelle'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Souscrire un abonnement</CardTitle>
                  <CardDescription>
                    Accès complet à tous les modules pour vos étudiants
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  {/* Sélection région */}
                  <div>
                    <Label className="text-base font-semibold mb-3 block">Votre région</Label>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(pricingConfig).map(([key, value]) => (
                        <button
                          key={key}
                          onClick={() => setFormData(prev => ({ ...prev, region: key }))}
                          className={`p-4 rounded-xl border-2 transition-all text-left ${
                            formData.region === key 
                              ? 'border-[#1B4965] bg-[#1B4965]/5' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-2xl font-bold text-[#1B4965]">
                            {value.basePrice}{value.currencySymbol}
                            <span className="text-sm font-normal text-gray-500">/mois/élève</span>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">{value.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Nombre d'étudiants */}
                  <div>
                    <Label htmlFor="studentCount" className="text-base font-semibold mb-3 block">
                      Nombre d'étudiants
                    </Label>
                    <Input
                      id="studentCount"
                      type="number"
                      min="1"
                      max="1000"
                      value={formData.studentCount}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        studentCount: parseInt(e.target.value) || 0 
                      }))}
                      className="text-lg"
                      data-testid="student-count-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Réduction de {(pricingConfig[formData.region]?.discountRate * 100) || 20}% à partir de {pricingConfig[formData.region]?.discountThreshold || 100} étudiants
                    </p>
                  </div>
                  
                  {/* Calcul du prix */}
                  {pricing && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gray-50 rounded-xl p-4"
                    >
                      <div className="flex justify-between text-sm mb-2">
                        <span>Prix unitaire</span>
                        <span>{pricing.unitPrice} {pricing.currency}/élève</span>
                      </div>
                      {pricing.discountApplied && (
                        <div className="flex justify-between text-sm mb-2 text-green-600">
                          <span>Réduction appliquée</span>
                          <span>-{(pricing.discountRate * 100).toFixed(0)}%</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm mb-2">
                        <span>Sous-total ({pricing.studentCount} élèves)</span>
                        <span>{pricing.subtotal} {pricing.currency}</span>
                      </div>
                      {pricing.vat > 0 && (
                        <div className="flex justify-between text-sm mb-2 text-gray-500">
                          <span>TVA ({(pricing.vatRate * 100).toFixed(0)}%)</span>
                          <span>{pricing.vat} {pricing.currency}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg pt-2 border-t">
                        <span>Total mensuel</span>
                        <span className="text-[#1B4965]">{pricing.total} {pricing.currency}</span>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Boutons paiement */}
                  <div className="space-y-3 pt-4">
                    {/* GoCardless - Prélèvement SEPA */}
                    <Button 
                      className="w-full bg-[#00D66E]/70 py-6 text-lg cursor-not-allowed"
                      disabled={true}
                      data-testid="gocardless-checkout-btn"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 6h18v12H3V6zm2 2v8h14V8H5zm2 2h10v1H7v-1zm0 3h7v1H7v-1z"/>
                      </svg>
                      Prélèvement SEPA - GoCardless (en cours d'installation)
                    </Button>
                    
                    {/* Stripe - Carte bancaire */}
                    <Button 
                      className="w-full bg-[#635BFF]/70 py-6 text-lg cursor-not-allowed"
                      disabled={true}
                      data-testid="stripe-checkout-btn"
                    >
                      <FaCreditCard className="mr-2" />
                      Carte bancaire - Stripe (en cours d'installation)
                    </Button>
                    
                    {/* PayPal */}
                    <Button 
                      className="w-full bg-[#0070BA]/70 py-6 text-lg cursor-not-allowed"
                      disabled={true}
                      data-testid="paypal-checkout-btn"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .761-.654h6.35c2.106 0 3.713.473 4.776 1.407 1.064.934 1.486 2.274 1.257 3.983-.23 1.709-.907 3.066-2.013 4.036-1.106.97-2.583 1.462-4.392 1.462h-2.07a.641.641 0 0 0-.633.545l-.904 5.838z"/>
                      </svg>
                      PayPal (en cours d'installation)
                    </Button>
                    
                    <p className="text-center text-xs text-gray-500 mt-2">
                      Paiement sécurisé • Sans engagement • Résiliable à tout moment
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Historique des transactions */}
            {transactions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FaHistory /> Historique des paiements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {transactions.map((tx, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            tx.status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'
                          }`}>
                            {tx.status === 'completed' ? (
                              <FaCheckCircle className="text-green-600" />
                            ) : (
                              <FaClock className="text-yellow-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{tx.amount} {tx.currency}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(tx.createdAt).toLocaleDateString('fr-FR')} • {tx.provider}
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">
                          <FaDownload />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          {/* Sidebar - Avantages */}
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-[#1B4965] to-[#2D5A7B] text-white">
              <CardContent className="p-6">
                <h3 className="font-bold text-xl mb-4">Inclus dans l'abonnement</h3>
                <ul className="space-y-3">
                  {[
                    'Quiz d\'actualité quotidiens (IA)',
                    '8 catégories de news',
                    'Atelier d\'écriture d\'articles',
                    'Simulateur d\'interview',
                    'Cours de grammaire',
                    'Formation SEO',
                    'Atelier OSINT',
                    'Entraînement frappe',
                    'Dashboard enseignant',
                    'Bulletins PDF automatiques',
                    'Journal public de l\'école',
                    'Support prioritaire'
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <FaCheckCircle className="text-[#FFB703] flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-4xl mb-2">💬</div>
                <h4 className="font-semibold mb-2">Une question ?</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Notre équipe est disponible pour vous accompagner
                </p>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.open('https://wa.me/33663211234', '_blank')}
                >
                  Nous contacter
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <FaDownload className="text-[#1B4965]" /> Guides utilisateur
                </h4>
                <div className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => downloadPDF('/guides/director', 'Guide_Directeur_DailyGlobe.pdf')}
                    data-testid="download-director-guide-btn"
                  >
                    📋 Guide Directeur (PDF)
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => downloadPDF('/guides/teacher', 'Guide_Enseignant_DailyGlobe.pdf')}
                    data-testid="download-teacher-guide-btn"
                  >
                    👩‍🏫 Guide Enseignant (PDF)
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
